import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Divider } from '@mui/material';
import { cloneDeep } from 'lodash';
import { ApiColumnType, type ApiAddress, type ApiEnumGroup, ApiItemTypes, type ApiOrganization, ApiCustomField, ApiItemData } from '@agunity/api-v4';

import AgButton from '../../../stories/Components/AgButton';
import AgTypography from '../../../stories/Styles/AgTypography';
import AgUploadAccountPhoto from '../../../stories/AgUploadFields/AgUploadAccountPhoto';
import AgTextArea from '../../../stories/Components/AgTextArea';
import AgTextField from '../../../stories/Components/AgTextField';
import AgToggle from '../../../stories/Components/AgToggle';
import AgDropdown from '../../../stories/AgDropdown';
import AgMultipleCustomField from '../../../stories/Components/Cards/AgMultipleCustomField';

import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setIsOpenViewAvatar, setOrgCountryOptions } from '../../../redux/slices/organizations';
import { CustomFieldGroupProps, DropdownOptionsProps } from '../types';
import { CustomFieldColumnType, MANDATORY_LEVEL, MEMBER_FIELD_NAME_NOTES, MEMBER_PARENT_FIELD_TYPE_NUMBER, ROOT_PATHS } from '../../../Constants';
import { getCountries } from '../../../redux/slices/countries';
import { AgColor } from '../../../stories/Styles/AgColor';

const EditOrganizationDetails = ({
  identity,
  setOrgDetails,
  imageURL,
  validationList,
  showError,
  onImageUpdate,
  tagList,
  enumList,
}: {
  identity?: ApiOrganization;
  // eslint-disable-next-line no-unused-vars
  setOrgDetails?: (data: ApiOrganization) => void;
  imageURL?: string;
  // eslint-disable-next-line no-unused-vars
  onImageUpdate?: (base64?: string) => void;
  validationList?: {[key: string]: string[]};
  showError?: boolean;
  tagList?: ApiCustomField[] | null;
  enumList?: ApiEnumGroup[];
}) => {
  const dispatch = useAppDispatch();
  const flags = useAppSelector((state) => state.auth.flags);
  const isFlagShowContent = !flags.includes('uploadOrganizationImage');
  const countryOptions: DropdownOptionsProps[] =
    useAppSelector((state) => state.staff.countryOptions)?.map((country) => ({
      id: country.id ?? -1,
      name: country.name ?? ''
    })) || [];
  const messages = useIntl().formatMessage;
  const latitude = identity?.address?.location?.Y?.toString() ?? '';
  const longitude = identity?.address?.location?.X?.toString() ?? '';
  const notes =
    identity?.data?.find((item) => item.key === MEMBER_FIELD_NAME_NOTES)
      ?.value || '';
  const [countries, setCountries] = useState<DropdownOptionsProps[]>(countryOptions);
  const [selectedCountry, setSelectedCountry] = useState<{
    name: string;
    id: number;
  }>();
  const [customFieldGroupData, setCustomFieldGroupData] = useState<CustomFieldGroupProps[]>([]);

  const saveProfileImage = async (base64?: string) => {
    onImageUpdate?.(base64);
  };

  const setOrgData = (data: ApiItemData[]) => {
    setOrgDetails?.({ ...identity, data });
  };

  useEffect(() => {
    if (!tagList || !tagList.length) return;
    const customFieldGroupData: CustomFieldGroupProps[]  = [];

    // Find all parent objects with MEMBER_PARENT_FIELD_TYPE_NUMBER
    const parentObjects = tagList.filter(
      (ta) =>
        ta.itemType === ApiItemTypes.Organization
    ).filter(obj => obj.fieldType === MEMBER_PARENT_FIELD_TYPE_NUMBER);

    // Iterate through each parent object
    parentObjects.forEach(parent => {
      const groupName = parent.key ?? '';
      const parentId = parent.id;

      // Find all child objects with matching parentId
      const childObjects = tagList.filter(obj => obj.parentId === parentId).map((item) => {
        const fieldValue = ((identity?.data || []).find(dataItem => dataItem.key === item.key) || {}).value || '';
        const defaultItem = ({
          name: item.key || '',
          value: fieldValue,
          unitId: item.unitId,
          mandatoryLevel: item.mandatoryLevel ?? MANDATORY_LEVEL.Optional,
          fieldType: item.fieldType ?? CustomFieldColumnType.None,
          ...(item.maxLength ? { maxLength: item.maxLength } : {})
        });

        if ([ApiColumnType.Enum, ApiColumnType.MultiEnum].includes(item.fieldType!)) {
          const id = parseInt(defaultItem?.name || '');
          if(id){
            const enu = enumList?.find((en) => en.id === id);
            const enuValues = enu?.values || [];
            let value = fieldValue;
            if(enuValues.length) {
              const values = fieldValue.split(',').map(Number);
              value = enuValues.filter(({ id }) => values.includes(id!)).map(({ name }) => name).join(',');
            }
            return {
              ...defaultItem,
              value,
            };
          }
        }
        return defaultItem;
      });

      // Push group object to customFieldGroupData array
      customFieldGroupData.push({
        groupName,
        data: childObjects
      });
    });

    setCustomFieldGroupData(customFieldGroupData);
  }, [identity?.data, tagList, enumList]);

  useEffect(() => {
    const f = async () => {
      try {
        if (countries?.length === 0) {
          const cou = await dispatch(getCountries());
          if (cou?.data?.length) {
            const countryList = cou.data.map((country) => ({
              name: country.name ?? '',
              id: country.id ?? -1
            }));
            setCountries(countryList);
            dispatch(setOrgCountryOptions(cou.data));
          }
        }
        if (countries.length && identity?.address?.countryId) {
          const countryObj = countries.find(
            (country) => country.id === identity?.address?.countryId
          );
          setSelectedCountry(countryObj);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };
    f();
  }, [countries, dispatch, identity?.address?.countryId]);

  return ( 
    <AgTypography
      style={{
        padding: '24px 16px',
        display: 'grid',
        gap: '16px'
      }}
    >
      <AgTextField
        label={messages({ id: 'CERTIFICATION_ORG_NAME' })}
        value={identity?.name ?? ''}
        onChange={(e) => {
          if (setOrgDetails && identity) {
            setOrgDetails({ ...identity, name: e.target.value });
          }
        }}
        error={
          Boolean(validationList?.name?.length) && showError
        }
        caption={
          showError ? validationList?.name : ''
        }
      />
      <AgTextArea
        label={messages({ id: 'DESCRIPTION' })}
        placeholder={messages({ id: 'FEEDBACK_MSG4' })}
        value={identity?.publicDescription ?? ''}
        onChange={(e) => {
          if (setOrgDetails && identity) {
            setOrgDetails({ ...identity, publicDescription: e.target.value });
          }
        }}
        error={
          Boolean(validationList?.publicDescription?.length) && showError
        }
        caption={
          showError ? validationList?.publicDescription?.[0] : ''
        }

      />
      <AgUploadAccountPhoto
        isFlagShowContent={isFlagShowContent}
        imageURL={imageURL}
        openAvatar={() => dispatch(setIsOpenViewAvatar(true))}
        saveProfileImage={saveProfileImage}
      />
      <AgTextArea
        optionalText
        label='Notes'
        placeholder={messages({ id: 'FEEDBACK_MSG4' })}
        value={notes}

        onChange={(e) => {
          if (setOrgDetails && identity) {
            const updatedIdentity = cloneDeep(identity);
            const hasNotes = updatedIdentity.data?.find(
              (item) =>
                item.key.toUpperCase() ===
                MEMBER_FIELD_NAME_NOTES.toUpperCase()
            );
            if (hasNotes) {
              hasNotes.value = e.target.value;
            } else {
              updatedIdentity.data?.push({
                key: MEMBER_FIELD_NAME_NOTES,
                value: e.target.value
              });
            }
            setOrgDetails(updatedIdentity);
          }
        }}
        error={
          Boolean(validationList?.notes?.length) && showError
        }
        caption={
          showError ? validationList?.notes?.[0] : ''
        }
      />
      <AgTypography
        contentStyle='AP.H5.UPPERCASE.Bold'
        message={messages({ id: 'MAKE_DETAILS_PUBLIC' })}
        style={{
          color: AgColor.grey6
        }}
      />
      <AgTypography
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <AgTypography
            contentStyle={'AP.Paragraph.Small.Regular'}
            // Will replace the text once the right text is available
            message={messages({ id: 'MAKE_DETAILS_PUBLIC_DESCRIPTION_1' })}
            style={{
              width: '320px',
              color: AgColor.grey7
            }}
          />
        </Box>
        <AgToggle
          checked={identity?.isMarketVisible ?? false}
          onChange={(value: boolean) => {
            if (setOrgDetails && identity) {
              setOrgDetails({ ...identity, isMarketVisible: value });
            }
          }}
        />
      </AgTypography>
      <AgTypography
        contentStyle={'AP.H5.UPPERCASE.Bold'}
        message={messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_TITLE' })}
        style={{ color: AgColor.grey6 }}
      />
      <AgTypography
        style={{
          display: 'flex',
          alignItems: 'start',
          gap: '16px'
        }}
      >
        <AgTypography
          style={{
            flex: 1
          }}
        >
          <AgDropdown
            inputLabel={messages({ id: 'MEMBER_PERSONAL_DETAILS_COUNTRY' })}
            /** TODO: Add country options */
            options={countries}
            sx={{ mt: 2 }}
            value={selectedCountry?.name ?? ''}
            onChange={(e: string) => {
              const selected = countries.find(
                (country) => country.name === e
              );
              setSelectedCountry(selected);
              if (setOrgDetails && identity) {
                setOrgDetails({
                  ...identity,
                  address: {
                    ...identity.address,
                    countryId: selected?.id ?? -1
                  }
                });
              }
            }}
            error={
              Boolean(validationList?.country?.length) && showError && !selectedCountry
            }
            caption={
              Boolean(validationList?.country?.length) && showError && !selectedCountry ? 
                validationList?.country?.[0] : 
                ''
            }
          />
        </AgTypography>
        <AgTextField
          optionalText
          label={messages({ id: 'MEMBER_PERSONAL_DETAILS_PROVINCE' })}
          formStyle={{ flex: 1 }}
          value={identity?.address?.province ?? ''}
          onChange={(e) => {
            if (setOrgDetails && identity) {
              setOrgDetails({
                ...identity,
                address: { ...identity.address, province: e.target.value } as ApiAddress
              });
            }
          }}
          error={
            Boolean(validationList?.province?.length) && showError
          }
          caption={
            Boolean(validationList?.province?.length) && showError
              ? validationList?.province
              : ''
          }
        />
      </AgTypography>
      <AgTypography
        style={{
          display: 'flex',
          alignItems: 'start',
          gap: '16px'
        }}
      >
        <AgTextField
          label={messages({ id: 'MEMBER_PERSONAL_DETAILS_CITY' })}
          formStyle={{ flex: 1 }}
          optionalText
          value={identity?.address?.city ?? ''}
          onChange={(e) => {
            if (setOrgDetails && identity) {
              setOrgDetails({
                ...identity,
                address: { ...identity.address, city: e.target.value } as ApiAddress
              });
            }
          }}
          error={
            Boolean(validationList?.city?.length) && showError
          }
          caption={
            Boolean(validationList?.city?.length) && showError
              ? validationList?.city
              : ''
          }
        />
        <AgTextField
          optionalText
          label={messages({ id: 'MEMBER_PERSONAL_DETAILS_POSTAL_CODE' })}
          formStyle={{ flex: 1 }}
          value={identity?.address?.postCode ?? ''}
          onChange={(e) => {
            if (setOrgDetails && identity) {
              setOrgDetails({
                ...identity,
                address: { ...identity.address, postCode: e.target.value } as ApiAddress
              });
            }
          }}
          error={
            Boolean(validationList?.postCode?.length) && showError
          }
          caption={
            Boolean(validationList?.postCode?.length) && showError
              ? validationList?.postCode
              : ''
          }

        />
      </AgTypography>
      <Divider sx={{ margin: '16px 0' }} />
      <AgTypography
        style={{
          display: 'grid',
          gap: '16px'
        }}
      >
        <AgTypography contentStyle={'AP.Paragraph.Small.Regular'}>
          <AgTypography
            contentStyle={'AP.Paragraph.Small.Regular'}
            style={{ color: AgColor.grey7 }}
            component={'span'}
          >
            {messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_TEXT_1' })}
          </AgTypography>
          {' '}
          <AgTypography
            contentStyle={'AP.Paragraph.Small.Link'}
            style={{ cursor: 'pointer' }}
            sx={{
              '&:hover': {
                color: AgColor.primaryMain
              }
            }}
            component={'span'}
            onClick={() => {
              window.open(ROOT_PATHS.HELP);
            }}
          >
            {messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_TEXT_2' })}
          </AgTypography>
          <AgTypography
            contentStyle={'AP.Paragraph.Small.Regular'}
            style={{ color: AgColor.grey7 }}
            component={'span'}
          >
            {messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_TEXT_DOT' })}
          </AgTypography>
        </AgTypography>

        <AgTypography
          style={{
            display: 'grid',
            gap: '16px'
          }}
        >
          <AgTextField
            optionalText
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_1' })}
            value={identity?.address?.addressLine1 ?? ''}
            formStyle={{ flex: 1 }}
            onChange={(e) => {
              if (setOrgDetails && identity) {
                setOrgDetails({
                  ...identity,
                  address: {
                    ...identity.address,
                    addressLine1: e.target.value
                  } as ApiAddress
                });
              }
            }}
            error={
              Boolean(validationList?.addressLine1?.length) && showError
            }
            caption={
              Boolean(validationList?.addressLine1?.length) && showError
                ? validationList?.addressLine1
                : ''
            }
          />
          <AgTextField
            optionalText
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_2' })}
            value={identity?.address?.addressLine2 ?? ''}
            formStyle={{ flex: 1 }}
            onChange={(e) => {
              if (setOrgDetails && identity) {
                setOrgDetails({
                  ...identity,
                  address: {
                    ...identity.address,
                    addressLine2: e.target.value
                  } as ApiAddress
                });
              }
            }}
            error={
              Boolean(validationList?.addressLine2?.length) && showError
            }
            caption={
              Boolean(validationList?.addressLine2?.length) && showError
                ? validationList?.addressLine2
                : ''
            }
          />
          <AgTextField
            optionalText
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_ADDRESS_3' })}
            value={identity?.address?.addressLine3 ?? ''}
            formStyle={{ flex: 1 }}
            onChange={(e) => {
              if (setOrgDetails && identity) {
                setOrgDetails({
                  ...identity,
                  address: {
                    ...identity.address,
                    addressLine3: e.target.value
                  } as ApiAddress
                });
              }
            }}
            error={
              Boolean(validationList?.addressLine3?.length) && showError
            }
            caption={
              Boolean(validationList?.addressLine3?.length) && showError
                ? validationList?.addressLine3
                : ''
            }
          />
        </AgTypography>
      </AgTypography>

      <Divider sx={{ margin: '16px 0' }} />

      <AgTypography
        style={{
          display: 'grid',
          gap: '16px'
        }}
      >
        <AgTypography
          contentStyle={'AP.H5.UPPERCASE.Bold'}
          message={messages({ id: 'GPS_LOCATION' })}
          style={{ color: AgColor.grey6 }}
        />
        <AgTypography
          style={{
            display: 'flex',
            alignItems: 'start',
            gap: '16px'
          }}
        >
          <AgTextField
            optionalText
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LATITUDE' })}
            placeholder={messages({
              id: 'MEMBER_PERSONAL_DETAILS_GPS_LATITUDE'
            })}
            formStyle={{ flex: 1 }}
            type={'number'}
            value={
              identity?.address?.location?.Y &&
              identity?.address?.location?.Y !== 0
                ? identity?.address?.location?.Y
                : ''
            }
            onChange={(e) => {
              if (setOrgDetails && identity) {
                setOrgDetails({
                  ...identity,
                  address: {
                    ...identity.address,
                    location: {
                      ...identity.address?.location,
                      Y: Number(e.target.value)
                    }
                  } as ApiAddress
                });
              }
            }}
            error={
              Boolean(validationList?.latitude?.length) && showError
            }
            caption={
              Boolean(validationList?.latitude?.length) && showError
                ? validationList?.latitude
                : ''
            }
          />
          <AgTextField
            optionalText
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_LONGITUDE' })}
            placeholder={messages({
              id: 'MEMBER_PERSONAL_DETAILS_GPS_LONGITUDE'
            })}
            formStyle={{ flex: 1 }}
            type={'number'}
            value={
              identity?.address?.location?.X &&
              identity?.address?.location?.X !== 0
                ? identity?.address?.location?.X
                : ''
            }
            onChange={(e) => {
              if (setOrgDetails && identity) {
                setOrgDetails({
                  ...identity,
                  address: {
                    ...identity.address,
                    location: {
                      ...identity.address?.location,
                      X: Number(e.target.value)
                    }
                  } as ApiAddress
                });
              }
            }}
            error={
              Boolean(validationList?.longitude?.length) && showError
            }
            caption={
              Boolean(validationList?.longitude?.length) && showError
                ? validationList?.longitude
                : ''
            }
          />
        </AgTypography>
        
        <AgTypography
          style={{
            display: 'flex',
            alignItems: 'start',
            gap: '16px'
          }}
        >
          <AgTextField
            optionalText
            label={messages({ id: 'MEMBER_PERSONAL_DETAILS_GPS_ALTITUDE' })}
            formStyle={{ flex: 1 }}
            type={'number'}
            value={identity?.address?.location?.altitude ?? ''}
            onChange={(e) => {
              if (setOrgDetails && identity) {
                setOrgDetails({
                  ...identity,
                  address: {
                    ...identity.address,
                    location: {
                      ...identity.address?.location,
                      altitude: Number(e.target.value)
                    }
                  } as ApiAddress
                });
              }
            }}
            error={
              Boolean(validationList?.altitude?.length) && showError
            }
            caption={
              Boolean(validationList?.altitude?.length) && showError
                ? validationList?.altitude
                : ''
            }
          />
          <AgTextField
            optionalText
            label={messages({ id: 'PRECISION' })}
            formStyle={{ flex: 1 }}
            type={'number'}
            value={identity?.address?.location?.accuracy ?? ''}
            onChange={(e) => {
              if (setOrgDetails && identity) {
                setOrgDetails({
                  ...identity,
                  address: {
                    ...identity.address,
                    location: {
                      ...identity.address?.location,
                      accuracy: Number(e.target.value)
                    }
                  } as ApiAddress
                });
              }
            }}
            error={
              Boolean(validationList?.precision?.length) && showError
            }
            caption={
              Boolean(validationList?.precision?.length) && showError
                ? validationList?.precision
                : ''
            }            
          />
        </AgTypography>
        <AgTypography
          style={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'end',
            gap: '16px'
          }}
        >

          <AgButton
            label={messages({ id: 'VIEW_GPS_LOCATION_ON_MAP' })}
            disabled={!Number(longitude) || !Number(latitude)}
            variant={'outlined'}
            color={'secondary'}
            onClick={() => {
              if (!longitude || !latitude) return;
              const mapUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
              window.open(mapUrl, '_blank');
            }}
          />
        </AgTypography>
      </AgTypography>

      <Divider sx={{ margin: '16px 0' }} />

      <AgTypography
        style={{
          display: 'grid',
          gap: '16px'
        }}
      >
        <AgTypography
          contentStyle={'AP.H5.UPPERCASE.Bold'}
          message={messages({ id: 'MEMBER_PERSONAL_DETAILS_CONTACT_TITLE' })}
          style={{ color: AgColor.grey6 }}
        />
        <AgTypography
          contentStyle={'AP.Paragraph.Small.Regular'}
          message={messages({ id: 'ORGANIZATION_CONTACT_FIELD_DETAILS' })}
          style={{ color: AgColor.grey7 }}
        />
        <AgTypography
          style={{
            display: 'grid',
            alignItems: 'center',
            gap: '16px'
          }}
        >
          <AgTextField
            optionalText
            label={messages({
              id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_PHONE'
            })}
            formStyle={{ flex: 1 }}
            value={identity?.phoneNumber ?? ''}
            onChange={(e) => {
              if (setOrgDetails && identity) {
                setOrgDetails({
                  ...identity,
                  phoneNumber: e.target.value
                });
              }
            }}
            error={
              Boolean(validationList?.phoneNumber?.length) && showError
            }
            caption={
              Boolean(validationList?.phoneNumber?.length) && showError
                ? validationList?.phoneNumber
                : ''
            }
          />
          <AgTextField
            optionalText
            label={messages({
              id: 'MEMBER_PERSONAL_DETAILS_CONTACT_INFO_EMAIL'
            })}
            formStyle={{ flex: 1 }}
            value={identity?.salesEmail ?? ''}
            onChange={(e) => {
              if (setOrgDetails && identity) {
                setOrgDetails({
                  ...identity,
                  salesEmail: e.target.value
                });
              }
            }}
            error={
              Boolean(validationList?.salesEmail?.length) && showError
            }
            caption={
              Boolean(validationList?.salesEmail?.length) && showError
                ? validationList?.salesEmail
                : ''
            }
          />
        </AgTypography>
      </AgTypography>
      {customFieldGroupData?.length ? <Divider sx={{ margin: '16px 0' }} /> : null}
      {customFieldGroupData?.length ? (
        <AgMultipleCustomField
          customFieldGroupData={customFieldGroupData}
          setOrgData={setOrgData}
          orgData={identity?.data || []}
          data={customFieldGroupData.map((customFieldData) => {
            return {
              title: customFieldData.groupName || '',
              editOnly: true,
              fields: customFieldData.data
            };
          })}
          mainWidth={'auto'}
          enumList={enumList}
        />
      ) : null}
    </AgTypography> );
};
 
export default EditOrganizationDetails;